import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { AlertCircle, AlertTriangle, X } from "lucide-react";
import SparklesGradientIcon from "@/assets/icons/SparklesGradientIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

/** Standalone axios instance without global interceptors (no Authorization header).
 *  S3 presigned URLs return 400 if an Authorization header is sent alongside
 *  query-string signature params. */
const plainHttp = axios.create();
import RecordingEntryDisplay from "@/modules/common/components/audio/RecordingEntryDisplay";
import { useAudioPlayback } from "@/modules/common/hooks/audio";
import {
  generateWaveformFromUrl,
  generateFallbackWaveform,
  WAVEFORM_BAR_COUNT,
  WaveformResult,
} from "@/modules/common/utils/audio.utils";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import { getZoomCallDetail } from "@/modules/lead-page/api/lead-page.v2.api";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import ComprehensiveAnalysis from "@/modules/zoom-calls/components/comprehensive-analysis";
import ZoomCallAiStatusButton from "@/modules/zoom-calls/components/zoom-call-ai-status-button";
import ZoomCallAiStatusInlineRow from "@/modules/zoom-calls/components/zoom-call-ai-status-inline-row";
import { ZoomCallAiStatusData } from "@/modules/zoom-calls/types";
import usePassport from "@/modules/common/hooks/passport";
import { PermissionsEnum, PermissionLevel } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

interface ZoomCallDetailModalProps {
  open: boolean;
  onClose: () => void;
  zoomCallUid: string;
  activityItem: ActivityItem;
}

interface TranscriptMessage {
  time: string;
  speaker: string;
  rawSpeaker?: string;
  text: string;
}

/* -------------------------------------------------------------------------- */
/*  Transcription parser                                                      */
/* -------------------------------------------------------------------------- */

/** Parse Zoom native JSON transcript.
 *  Supports: { diarization: { timeline: [...] } }, { timeline: [...] }, or { diarization: [...] } */
const parseZoomJsonTranscript = (data: any): TranscriptMessage[] | null => {
  // Resolve the timeline array — check diarization first, then top-level
  const timeline =
    data?.diarization?.timeline ?? data?.diarization ?? data?.timeline;
  if (!Array.isArray(timeline)) return null;

  const messages: TranscriptMessage[] = [];
  for (const entry of timeline) {
    const text = entry.text || entry.content || entry.message;
    if (!text) continue;

    // Resolve speaker — try common field names. Preserve the raw label so
    // analysis-driven speaker_mapping can resolve it to Agent/Client later.
    const rawSpeaker =
      entry.zoom_userid ||
      entry.users?.[0]?.zoom_userid ||
      entry.username ||
      entry.speaker_name ||
      entry.speaker ||
      entry.user ||
      null;
    const isUnknown =
      !rawSpeaker ||
      rawSpeaker === "Unknown Speaker" ||
      entry.zoom_userid === "Unknown Speaker" ||
      entry.users?.[0]?.zoom_userid === "Unknown Speaker";
    const speaker = isUnknown ? "Customer" : rawSpeaker;

    // Resolve timestamp — try common field names
    const rawTime =
      entry.ts ||
      entry.start_time ||
      entry.start ||
      entry.time ||
      entry.timestamp ||
      "";
    const time = String(rawTime).split(".")[0];

    messages.push({ time, speaker, rawSpeaker: rawSpeaker ?? undefined, text });
  }
  return messages.length > 0 ? messages : null;
};

const parseTranscription = (raw: string): TranscriptMessage[] | null => {
  // Try parsing as JSON first (Zoom native transcript format)
  try {
    const json = JSON.parse(raw);
    if (
      json?.type === "zoom_transcript" ||
      json?.timeline ||
      json?.diarization
    ) {
      const result = parseZoomJsonTranscript(json);
      if (result) return result;
    }
  } catch {
    // Not JSON — continue with text-based parsing
  }

  const lines = raw.trim().split("\n");
  const messages: TranscriptMessage[] = [];

  // Try format: [HH:MM:SS] Speaker: message
  const bracketRegex = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.+?):\s*(.+)$/;
  for (const line of lines) {
    const match = line.trim().match(bracketRegex);
    if (match) {
      messages.push({
        time: match[1],
        speaker: match[2],
        rawSpeaker: match[2],
        text: match[3],
      });
    }
  }
  if (messages.length > 0) return messages;

  // Try WebVTT-like format
  const vttTimestampRegex =
    /^(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?)\s*-->\s*\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?$/;
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (
      !trimmed ||
      trimmed === "WEBVTT" ||
      trimmed === "NOTE" ||
      /^\d+$/.test(trimmed)
    ) {
      i++;
      continue;
    }

    const tsMatch = trimmed.match(vttTimestampRegex);
    if (tsMatch) {
      const time = tsMatch[1];
      i++;
      while (i < lines.length && !lines[i].trim()) i++;
      if (i >= lines.length) break;

      const speakerOrText = lines[i].trim();
      i++;

      const textParts: string[] = [];
      while (i < lines.length) {
        const next = lines[i].trim();
        if (!next || vttTimestampRegex.test(next) || /^\d+$/.test(next)) break;
        textParts.push(next);
        i++;
      }

      if (textParts.length > 0) {
        const sp = speakerOrText.replace(/:$/, "");
        messages.push({
          time,
          speaker: sp,
          rawSpeaker: sp,
          text: textParts.join(" "),
        });
      } else {
        messages.push({ time, speaker: "Speaker", text: speakerOrText });
      }
      continue;
    }

    i++;
  }
  if (messages.length > 0) return messages;

  // Try simple "Speaker: message" per line
  const simpleRegex = /^(.+?):\s+(.+)$/;
  for (const line of lines) {
    const match = line.trim().match(simpleRegex);
    if (match) {
      messages.push({
        time: "",
        speaker: match[1],
        rawSpeaker: match[1],
        text: match[2],
      });
    }
  }
  if (messages.length > 0) return messages;

  return null;
};

/** Format transcript time to "M:SS" or "H:MM:SS".
 *  Handles: seconds as number/string (e.g. 65 -> "1:05"), "0:05:23" -> "5:23", "0:01" kept as-is */
const formatTranscriptTime = (time: string): string => {
  // If it's a plain number (seconds), convert to M:SS
  const num = Number(time);
  if (!isNaN(num) && !time.includes(":")) {
    const totalSec = Math.floor(num);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  const parts = time.split(":");
  if (parts.length === 3) {
    // H:MM:SS — drop leading hour if 0
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const s = parts[2].split(".")[0]; // strip ms
    if (h === 0) return `${m}:${s.padStart(2, "0")}`;
    return `${h}:${parts[1]}:${s.padStart(2, "0")}`;
  }
  // M:SS — keep as-is
  return time.split(".")[0];
};

/** Parse a transcript time string into seconds.
 *  Accepts: plain number ("65"), "M:SS", "H:MM:SS", with optional ".ms" suffix. */
const parseTimeToSeconds = (time: string): number => {
  if (!time) return 0;
  const num = Number(time);
  if (!isNaN(num) && !time.includes(":")) return num;

  const [mainPart] = time.split(".");
  const parts = mainPart.split(":").map((p) => parseInt(p, 10));
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const getInitials = (name: string): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
};

const formatLabel = (val: string) =>
  val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

/** Single transcript row — Figma: 14px, flex-wrap, gap 4px/8px */
const TranscriptRow = ({
  msg,
  isActive,
  onClick,
  rowRef,
}: {
  msg: TranscriptMessage;
  isActive?: boolean;
  onClick?: () => void;
  rowRef?: (el: HTMLDivElement | null) => void;
}) => {
  const theme = useTheme();

  return (
    <Box
      ref={rowRef}
      onClick={onClick}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px 8px",
        alignItems: "flex-start",
        p: "8px",
        borderRadius: theme.nawy.radius.sm,
        cursor: onClick ? "pointer" : "default",
        backgroundColor: isActive
          ? theme.nawy.colors.primitives.blue[50]
          : "transparent",
        transition: "background-color 0.15s",
        "&:hover": onClick
          ? { backgroundColor: theme.nawy.colors.semantic.layer.secondary }
          : undefined,
      }}
    >
      {/* Timestamp */}
      {msg.time && (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.secondary,
            flexShrink: 0,
            width: 32,
          }}
        >
          {formatTranscriptTime(msg.time)}
        </Typography>
      )}

      {/* Speaker name (bold) */}
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.semibold,
          color: theme.nawy.colors.semantic.text.primary,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {msg.speaker}
      </Typography>

      {/* Message text */}
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.regular,
          color: theme.nawy.colors.semantic.text.primary,
          wordBreak: "break-word",
          flex: "1 0 0",
          minWidth: 0,
        }}
      >
        {msg.text}
      </Typography>
    </Box>
  );
};

/* -------------------------------------------------------------------------- */
/*  Main component                                                            */
/* -------------------------------------------------------------------------- */

const DRAWER_WIDTH = "460px";
const KEY_POINTS_PREVIEW = 3;

const ZoomCallDetailModal = ({
  open,
  onClose,
  zoomCallUid,
  activityItem,
}: ZoomCallDetailModalProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const { permissions: currentUserPermissions } = usePassport();
  const canViewAnalysis =
    userHasPermission(
      { name: PermissionsEnum.ZOOM_CALLS_ADMIN, level: PermissionLevel.READ },
      currentUserPermissions,
    ) ||
    userHasPermission(
      { name: PermissionsEnum.ZOOM_CALL_ANALYSIS, level: PermissionLevel.READ },
      currentUserPermissions,
    );

  // Audio playback
  const {
    togglePlay,
    isPlaying,
    getCurrentTimeForId,
    getDurationForId,
    isLoading: audioLoading,
    getErrorForId,
    playingId,
    loadedId,
    seekTo,
    playbackRate,
    setPlaybackRate,
  } = useAudioPlayback();

  const [waveform, setWaveform] = useState<WaveformResult | null>(null);
  const [keyPointsExpanded, setKeyPointsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "analysis">("overview");

  // 1. Fetch zoom call detail from backend
  const {
    data: callDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch,
  } = useQuery({
    queryKey: ["zoom-call-detail", zoomCallUid],
    queryFn: () => getZoomCallDetail(zoomCallUid),
    enabled: open && !!zoomCallUid,
    staleTime: 5 * 60 * 1000,
  });

  const recordingUrl = callDetail?.presignedRecordingUrl;

  const transcriptionUrl = callDetail?.presignedTranscriptionUrl;

  // 2. Fetch transcription text from presigned URL
  const {
    data: transcriptionText,
    isError: isTranscriptionError,
    fetchStatus: transcriptionFetchStatus,
  } = useQuery({
    queryKey: ["zoom-call-detail-transcription", transcriptionUrl],
    queryFn: async () => {
      const { data } = await plainHttp.get<string>(transcriptionUrl!, {
        responseType: "text",
      });
      return data;
    },
    enabled: !!transcriptionUrl && !isDetailLoading,
    staleTime: 30 * 60 * 1000,
  });

  // Generate waveform when recording URL is available
  useEffect(() => {
    if (recordingUrl) {
      generateWaveformFromUrl(recordingUrl, WAVEFORM_BAR_COUNT).then(
        setWaveform,
      );
    }
  }, [recordingUrl]);

  const playbackId = `zoom-detail-${zoomCallUid}`;

  const recordingEntry: RecordingEntry | null = recordingUrl
    ? {
        id: playbackId,
        audioBlob: null,
        audioUrl: recordingUrl,
        duration:
          getDurationForId(playbackId) > 0
            ? getDurationForId(playbackId)
            : waveform?.duration || 0,
        waveformData:
          waveform?.waveformData ||
          generateFallbackWaveform(WAVEFORM_BAR_COUNT),
      }
    : null;

  // Parse transcription
  const parsedTranscript = useMemo(
    () => (transcriptionText ? parseTranscription(transcriptionText) : null),
    [transcriptionText],
  );

  // Analysis data
  const sentiment = callDetail?.sentiment || activityItem.zoomCallSentiment;
  const summary = callDetail?.summary || activityItem.zoomCallSummary;
  const flags = callDetail?.flags as Record<string, any> | null | undefined;
  const callOutcome = flags?.call_outcome as string | undefined;
  const redFlags = flags?.red_flags as string[] | undefined;
  const requiresEscalation = flags?.requires_escalation as boolean | undefined;
  const escalationReason = flags?.escalation_reason as string | undefined;
  const speakerMapping = flags?.speaker_mapping as
    | Record<string, string>
    | undefined;

  const keyPoints = useMemo<string[]>(
    () =>
      (summary ?? []).filter(
        (s): s is string => typeof s === "string" && s.trim().length > 0,
      ),
    [summary],
  );

  // Sentiment chips to display
  const sentimentChips = useMemo(() => {
    const chips: Array<{ label: string; isArc?: boolean }> = [];
    if (sentiment?.overall_client) {
      chips.push({ label: `Client ${sentiment.overall_client}` });
    }
    if (sentiment?.overall_agent) {
      chips.push({ label: `Agent ${sentiment.overall_agent}` });
    }
    if (sentiment?.arc) {
      chips.push({ label: sentiment.arc.replace(/_/g, " "), isArc: true });
    }
    return chips;
  }, [sentiment]);

  const isLoading = isDetailLoading;

  // Agent info
  const agentName = callDetail?.agentName || activityItem.actorName;
  const customerName = callDetail?.customer?.name;
  const callDate = callDetail?.createdAt || activityItem.createdAt;
  const formattedDate = callDate
    ? format(parseISO(callDate), "d MMM yyyy, h:mm a")
    : "";

  // Map raw transcript speaker labels to roles using speaker_mapping (sourced
  // from zoom_call_analyses.extra.speaker_mapping on the backend), then resolve
  // roles ("Agent"/"Client") and the unknown-speaker fallback to real names.
  const mappedTranscript = useMemo(() => {
    if (!parsedTranscript) return null;
    return parsedTranscript.map((msg) => {
      const key = msg.rawSpeaker ?? msg.speaker;
      const role = key ? speakerMapping?.[key] : undefined;
      let speaker = msg.speaker;
      if (role === "Agent") speaker = agentName || "Agent";
      else if (role === "Client") speaker = customerName || "Customer";
      else if (role) speaker = role;
      else if (msg.speaker === "Customer")
        speaker = customerName || "Customer";
      return { ...msg, speaker };
    });
  }, [parsedTranscript, speakerMapping, agentName, customerName]);

  const hasSummaryContent = keyPoints.length > 0 || sentimentChips.length > 0;
  const hasTranscriptContent =
    recordingEntry || transcriptionUrl || transcriptionText;

  // Pre-compute each transcript message's start time in seconds
  const transcriptStartTimes = useMemo(
    () => (mappedTranscript ?? []).map((m) => parseTimeToSeconds(m.time)),
    [mappedTranscript],
  );

  // Find the active transcript index based on current playback time
  const currentPlaybackTime = getCurrentTimeForId(playbackId);
  const activeTranscriptIdx = useMemo(() => {
    if (!isPlaying(playbackId) || transcriptStartTimes.length === 0) return -1;
    let idx = -1;
    for (let i = 0; i < transcriptStartTimes.length; i++) {
      if (transcriptStartTimes[i] <= currentPlaybackTime) idx = i;
      else break;
    }
    return idx;
  }, [currentPlaybackTime, transcriptStartTimes, isPlaying, playbackId]);

  // Auto-scroll the active transcript row into view
  const transcriptRowsRef = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => {
    if (activeTranscriptIdx < 0) return;
    const el = transcriptRowsRef.current[activeTranscriptIdx];
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [activeTranscriptIdx]);

  // Detect whether the sticky audio player is currently stuck (scrolled past
  // its natural position) so we can apply drawer-style wrapper only on scroll.
  const stickySentinelRef = useRef<HTMLDivElement | null>(null);
  const [isPlayerStuck, setIsPlayerStuck] = useState(false);
  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsPlayerStuck(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [recordingEntry]);

  /** Figma: roundness/xl = 12px */
  const cardRadius = "12px";

  /* ----- Shared content --------------------------------------------------- */

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.nawy.spacing.sm,
            py: theme.nawy.spacing.xl,
          }}
        >
          <CircularProgress size={28} />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
            }}
          >
            Loading call details...
          </Typography>
        </Box>
      );
    }

    if (isDetailError) {
      return (
        <Box
          role="alert"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            py: theme.nawy.spacing.xl,
            textAlign: "center",
          }}
        >
          <AlertTriangle
            size={28}
            color={theme.nawy.colors.semantic.indicator.red}
            aria-hidden
          />
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.semibold,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Couldn&apos;t load call details
          </Typography>
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.text.tertiary,
              maxWidth: 280,
            }}
          >
            Something went wrong. Try again in a moment.
          </Typography>
          <Typography
            component="button"
            onClick={() => refetch()}
            sx={{
              mt: theme.nawy.spacing.xs,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              ...theme.nawy.typography.english.variants.body.sm.semibold,
              color: theme.nawy.colors.semantic.indicator.navy,
              textDecoration: "underline",
              "&:hover": { opacity: 0.8 },
            }}
          >
            Try again
          </Typography>
        </Box>
      );
    }

    const p = theme.nawy.colors.primitives;

    const tabsStrip = canViewAnalysis ? (
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          minHeight: 36,
          mb: theme.nawy.spacing.sm,
          borderBottom: `1px solid ${p.navy[50]}`,
          "& .MuiTab-root": {
            minHeight: 36,
            textTransform: "none",
            fontSize: 13,
            fontWeight: 600,
            color: p.grey[700],
            px: theme.nawy.spacing.sm,
          },
          "& .Mui-selected": {
            color: `${p.navy[800]} !important`,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: p.blue[500],
          },
        }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="analysis" label="Analysis" />
      </Tabs>
    ) : null;

    if (canViewAnalysis && activeTab === "analysis") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {tabsStrip}
          <ComprehensiveAnalysis
            flags={flags}
            sentiment={sentiment}
            extractedFields={callDetail?.extractedFields}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xs,
        }}
      >
        {tabsStrip}

        {/* ── AI Suggested Status ── */}
        {callDetail && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: theme.nawy.spacing.xs }}>
            <ZoomCallAiStatusButton
              zoomCallUid={callDetail.uid}
              aiStatus={(callDetail.aiStatus ?? null) as ZoomCallAiStatusData | null}
              variant="full"
            />
            <ZoomCallAiStatusInlineRow
              aiStatus={(callDetail.aiStatus ?? null) as ZoomCallAiStatusData | null}
              variant="full"
            />
          </Box>
        )}

        {/* ── Summary card ── */}
        {hasSummaryContent && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              px: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.xs,
              borderRadius: cardRadius,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            {/* "Call summary" label + sparkles icon */}
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                height: 20,
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 400,
                  lineHeight: "16px",
                  color: theme.nawy.colors.semantic.text.tertiary,
                  whiteSpace: "nowrap",
                }}
              >
                Call summary
              </Typography>
              <SparklesGradientIcon size={16} />
            </Box>

            {/* Sentiment */}
            {sentimentChips.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {/* Client / Agent chips */}
                {sentimentChips.some((c) => !c.isArc) && (
                  <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap", minWidth: 0, maxWidth: "100%" }}>
                    {sentimentChips.filter((c) => !c.isArc).map((chip, idx) => (
                      <Chip
                        key={idx}
                        label={chip.label}
                        size="small"
                        sx={{
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: "18px",
                          color: theme.nawy.colors.semantic.text.primary,
                          backgroundColor: theme.nawy.colors.semantic.layer.primary,
                          borderRadius: "4px",
                          height: "auto",
                          maxWidth: "100%",
                          "& .MuiChip-label": {
                            px: theme.nawy.spacing.xs,
                            py: "2px",
                            whiteSpace: "normal",
                            overflowWrap: "anywhere",
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* Arc — plain text, no bullet */}
                {sentimentChips.filter((c) => c.isArc).map((chip, idx) => (
                  <Typography
                    key={idx}
                    sx={{
                      ...theme.nawy.typography.english.variants.body.md.regular,
                      color: theme.nawy.colors.semantic.text.primary,
                    }}
                  >
                    {chip.label}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Key points list */}
            {keyPoints.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  {(keyPointsExpanded
                    ? keyPoints
                    : keyPoints.slice(0, KEY_POINTS_PREVIEW)
                  ).map((point, idx) => (
                    <Typography
                      key={idx}
                      component="li"
                      sx={{
                        ...theme.nawy.typography.english.variants.body.md.regular,
                        color: theme.nawy.colors.semantic.text.primary,
                      }}
                    >
                      {point}
                    </Typography>
                  ))}
                </Box>
                {keyPoints.length > KEY_POINTS_PREVIEW && (
                  <Button
                    onClick={() => setKeyPointsExpanded((prev) => !prev)}
                    sx={{
                      mt: 0.5,
                      p: 0,
                      fontSize: "0.75rem",
                      textTransform: "none",
                      color: theme.nawy.colors.semantic.text.accent,
                      minWidth: "auto",
                      "&:hover": { background: "transparent" },
                    }}
                  >
                    {keyPointsExpanded
                      ? "View less"
                      : `View more (${keyPoints.length - KEY_POINTS_PREVIEW} more)`}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* ── Call outcome card ── */}
        {callOutcome && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              px: "12px",
              py: theme.nawy.spacing.sm,
              borderRadius: cardRadius,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.secondary,
                whiteSpace: "nowrap",
              }}
            >
              Call outcome
            </Typography>
            <Chip
              label={formatLabel(callOutcome)}
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                backgroundColor: "#015c9a",
                color: "#fff",
                borderRadius: "4px",
                height: "auto",
                "& .MuiChip-label": { px: theme.nawy.spacing.xs, py: "2px" },
              }}
            />
          </Box>
        )}

        {/* ── Escalation banner ── */}
        {requiresEscalation && (
          <Box
            sx={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              p: "12px",
              borderRadius: cardRadius,
              backgroundColor: "#feeceb",
            }}
          >
            <AlertCircle
              size={16}
              color={theme.nawy.colors.semantic.indicator.red}
              style={{ flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "18px",
                color: theme.nawy.colors.semantic.text.primary,
              }}
            >
              {escalationReason
                ? `Escalation ${escalationReason.charAt(0).toLowerCase()}${escalationReason.slice(1)}`
                : "Requires escalation"}
            </Typography>
          </Box>
        )}

        {/* ── Red flags card ── */}
        {redFlags && redFlags.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              p: "12px",
              borderRadius: cardRadius,
              backgroundColor: "#fff6f0",
            }}
          >
            {redFlags.map((flag, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <AlertCircle
                  size={16}
                  color={
                    theme.nawy.colors.semantic.indicator.orange ?? "#b34b00"
                  }
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: "18px",
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {flag}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* ── Transcript card (audio + transcript rows) ── */}
        {hasTranscriptContent && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.xs,
              px: theme.nawy.spacing.sm,
              py: theme.nawy.spacing.xs,
              borderRadius: cardRadius,
              backgroundColor: theme.nawy.colors.primitives.white[200],
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 400,
                lineHeight: "16px",
                color: theme.nawy.colors.semantic.text.tertiary,
                whiteSpace: "nowrap",
              }}
            >
              Transcript
            </Typography>

            {/* Sentinel — used to detect when sticky player is stuck */}
            {recordingEntry && (
              <Box ref={stickySentinelRef} sx={{ height: 0 }} />
            )}

            {/* Audio player — sticky to top of scroll container */}
            {recordingEntry && (
              <Box
                sx={{
                  position: "sticky",
                  top: `-${theme.nawy.spacing.md}`,
                  zIndex: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: isPlayerStuck
                    ? theme.nawy.colors.semantic.layer.primary
                    : "transparent",
                  mx: isPlayerStuck
                    ? `calc(-${theme.nawy.spacing.sm} - ${theme.nawy.spacing.md})`
                    : 0,
                  px: 0,
                  py: isPlayerStuck ? theme.nawy.spacing.sm : 0,
                  borderBottom: isPlayerStuck
                    ? `1px solid ${theme.nawy.colors.primitives.navy[50]}`
                    : "none",
                  transition: "background-color 0.15s",
                }}
              >
                <RecordingEntryDisplay
                  isActivity
                  recording={recordingEntry}
                  isPlaying={isPlaying(playbackId)}
                  currentPlaybackTime={getCurrentTimeForId(playbackId)}
                  onTogglePlay={() => togglePlay(playbackId, recordingUrl!)}
                  theme={theme}
                  isLoading={
                    audioLoading &&
                    playingId === null &&
                    loadedId !== playbackId
                  }
                  error={getErrorForId(playbackId)}
                  onSeek={(time) => seekTo(playbackId, time)}
                  playbackRate={playbackRate}
                  onPlaybackRateChange={setPlaybackRate}
                />
              </Box>
            )}

            {/* Transcript rows */}
            {transcriptionFetchStatus === "fetching" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: theme.nawy.spacing.lg,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {isTranscriptionError &&
              transcriptionFetchStatus !== "fetching" && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.tertiary,
                    fontStyle: "italic",
                    textAlign: "center",
                    py: theme.nawy.spacing.lg,
                  }}
                >
                  Failed to load transcription.
                </Typography>
              )}

            {transcriptionFetchStatus !== "fetching" &&
              !isTranscriptionError &&
              !transcriptionText &&
              !transcriptionUrl && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                    color: theme.nawy.colors.semantic.text.tertiary,
                    fontStyle: "italic",
                    textAlign: "center",
                    py: theme.nawy.spacing.lg,
                  }}
                >
                  No transcription available for this call.
                </Typography>
              )}

            {transcriptionFetchStatus !== "fetching" &&
              !isTranscriptionError &&
              transcriptionText && (
                <>
                  {mappedTranscript ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: theme.nawy.spacing.xs,
                      }}
                    >
                      {mappedTranscript.map((msg, idx) => (
                        <TranscriptRow
                          key={idx}
                          msg={msg}
                          isActive={idx === activeTranscriptIdx}
                          rowRef={(el) => {
                            transcriptRowsRef.current[idx] = el;
                          }}
                          onClick={
                            recordingEntry
                              ? () => {
                                  const seconds = transcriptStartTimes[idx];
                                  if (loadedId !== playbackId) {
                                    togglePlay(playbackId, recordingUrl!);
                                  }
                                  seekTo(playbackId, seconds);
                                }
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      component="pre"
                      sx={{
                        ...theme.nawy.typography.english.variants.body.md
                          .regular,
                        color: theme.nawy.colors.semantic.text.primary,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        margin: 0,
                        fontFamily: "inherit",
                      }}
                    >
                      {transcriptionText}
                    </Box>
                  )}
                </>
              )}
          </Box>
        )}

        {/* ── Agent footer (inline, not sticky) ── */}
        {agentName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: theme.nawy.spacing.xs,
            }}
          >
            {/* Monogram — 24px circle */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.nawy.colors.semantic.indicator.navyLight,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  lineHeight: "18px",
                  color: theme.nawy.colors.semantic.indicator.navy,
                }}
              >
                {getInitials(agentName).charAt(0)}
              </Typography>
            </Box>
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.body.sm.regular,
                color: theme.nawy.colors.semantic.text.secondary,
              }}
            >
              {agentName} called lead{formattedDate ? ` ${formattedDate}` : ""}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  /* ----- Mobile: bottom sheet --------------------------------------------- */

  if (isMobile) {
    return (
      <MobileModal
        open={open}
        onClose={onClose}
        title="Call details"
        snapPoints={[1]}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            width: "100%",
            px: theme.nawy.spacing.md,
            py: theme.nawy.spacing.sm,
          }}
        >
          {renderContent()}
        </Box>
      </MobileModal>
    );
  }

  /* ----- Desktop: right-anchored Drawer ----------------------------------- */

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 1400,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1400,
        },
      }}
    >
      {/* Header — 52px, "Call details" + X, border-bottom */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.nawy.spacing.md,
          height: 52,
          px: theme.nawy.spacing.md,
          py: theme.nawy.spacing.xs,
          borderBottom: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            ...theme.nawy.typography.english.variants.headline.xs,
            color: theme.nawy.colors.semantic.text.primary,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Call details
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            color: theme.nawy.colors.semantic.text.primary,
            flexShrink: 0,
            px: "10px",
            "&:hover": {
              backgroundColor: theme.nawy.colors.semantic.layer.secondary,
            },
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: theme.nawy.spacing.md,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: theme.nawy.colors.semantic.border.secondary,
            borderRadius: 3,
          },
        }}
      >
        {renderContent()}
      </Box>
    </Drawer>
  );
};

export default ZoomCallDetailModal;
